import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Computer as ComputerIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminIcon,
  SupervisedUserCircle as SuperAdminIcon,
  Circle as CircleIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
  CloudUpload as DeploymentIcon,
} from '@mui/icons-material';
import DeploymentTab from './deploymentPanel.js';

const ensureHttps = (url) => {
  if (url.startsWith('/')) return url;
  return url.replace(/^http:/, 'https:');
};

const secureFetch = async (url, options = {}) => {
  const secureUrl = ensureHttps(url);
  return fetch(secureUrl, {
    ...options,
    credentials: 'same-origin',
  });
};

export default function AdminPanel({ users, devices, onRefresh }) {
  const [activeTab, setActiveTab] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [allDevices, setAllDevices] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [editUserDialog, setEditUserDialog] = useState(null);
  const [createOrgDialog, setCreateOrgDialog] = useState(false);
  const [editOrgDialog, setEditOrgDialog] = useState(null);
  const [deleteOrgDialog, setDeleteOrgDialog] = useState(null);
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(null);
  const [deleteUserDialog, setDeleteUserDialog] = useState(null);
  const [newOrgName, setNewOrgName] = useState('');
  const [editOrgName, setEditOrgName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserOrg, setNewUserOrg] = useState('');
  const [resetPassword, setResetPassword] = useState('');

  // Roles state
  const [roles, setRoles] = useState([]);
  const [createRoleDialog, setCreateRoleDialog] = useState(false);
  const [editRoleDialog, setEditRoleDialog] = useState(null);
  const [editPermissionsDialog, setEditPermissionsDialog] = useState(null);
  const [deleteRoleDialog, setDeleteRoleDialog] = useState(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [assignRoleDialog, setAssignRoleDialog] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [userDeviceRolesDialog, setUserDeviceRolesDialog] = useState(null);
  const [userDeviceRoles, setUserDeviceRoles] = useState([]);

  useEffect(() => {
    loadAllDevices();
    const interval = setInterval(loadAllDevices, 2500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadCurrentUser();
    loadAllDevices();
  }, []);

  useEffect(() => {
    if (currentUser?.IsSuperAdmin) {
      loadOrganizations();
    }
    if (currentUser?.IsAdmin || currentUser?.IsSuperAdmin) {
      loadRoles();
    }
  }, [currentUser]);

  const loadCurrentUser = async () => {
    try {
      const response = await secureFetch('/auth/me');
      if (response.ok) {
        setCurrentUser(await response.json());
      }
    } catch (error) {
      console.error('Failed to load current user:', error);
    }
  };

  const loadAllDevices = async () => {
    setLoading(true);
    try {
      const response = await secureFetch('/admin/devices');
      if (response.ok) {
        setAllDevices(await response.json());
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
    setLoading(false);
  };

  const loadOrganizations = async () => {
    try {
      const response = await secureFetch('/admin/organizations');
      if (response.ok) {
        setOrganizations(await response.json());
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await secureFetch('/admin/roles');
      if (response.ok) {
        setRoles(await response.json());
      }
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  };

  const createRole = async () => {
    if (!newRoleName.trim()) return;
    try {
      const response = await secureFetch('/admin/roles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newRoleName,
          description: newRoleDescription || null,
          organizationId: currentUser?.OrganizationId || null,
          isSystemRole: false
        })
      });
      if (response.ok) {
        setNewRoleName('');
        setNewRoleDescription('');
        setCreateRoleDialog(false);
        loadRoles();
      }
    } catch (error) {
      console.error('Failed to create role:', error);
    }
  };

  const updateRole = async () => {
    if (!editRoleDialog) return;
    try {
      const response = await secureFetch(`/admin/roles/${editRoleDialog.Id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newRoleName || editRoleDialog.Name,
          description: newRoleDescription
        })
      });
      if (response.ok) {
        setNewRoleName('');
        setNewRoleDescription('');
        setEditRoleDialog(null);
        loadRoles();
      }
    } catch (error) {
      console.error('Failed to update role:', error);
    }
  };

  const deleteRole = async () => {
    if (!deleteRoleDialog) return;
    try {
      const response = await secureFetch(`/admin/roles/${deleteRoleDialog.Id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setDeleteRoleDialog(null);
        loadRoles();
      }
    } catch (error) {
      console.error('Failed to delete role:', error);
    }
  };

  const updateRolePermissions = async () => {
    if (!editPermissionsDialog) return;
    try {
      const response = await secureFetch(`/admin/roles/${editPermissionsDialog.Id}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: selectedPermissions })
      });
      if (response.ok) {
        setEditPermissionsDialog(null);
        setSelectedPermissions([]);
        loadRoles();
      }
    } catch (error) {
      console.error('Failed to update role permissions:', error);
    }
  };

  const assignUserRole = async (userId, roleId) => {
    try {
      const response = await secureFetch(`/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roleId })
      });
      if (response.ok) {
        setAssignRoleDialog(null);
        setSelectedRoleId('');
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to assign role:', error);
    }
  };

  const removeUserRole = async (userId) => {
    try {
      const response = await secureFetch(`/admin/users/${userId}/role`, {
        method: 'DELETE'
      });
      if (response.ok) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to remove user role:', error);
    }
  };

  const loadUserDeviceRoles = async (userId) => {
    try {
      const response = await secureFetch(`/admin/users/${userId}/device-roles`);
      if (response.ok) {
        setUserDeviceRoles(await response.json());
      }
    } catch (error) {
      console.error('Failed to load user device roles:', error);
    }
  };

  const setUserDeviceRole = async (userId, deviceId, roleId) => {
    try {
      const response = await secureFetch(`/admin/users/${userId}/device-roles/${deviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roleId })
      });
      if (response.ok) {
        loadUserDeviceRoles(userId);
      }
    } catch (error) {
      console.error('Failed to set device role:', error);
    }
  };

  const removeUserDeviceRole = async (userId, deviceId) => {
    try {
      const response = await secureFetch(`/admin/users/${userId}/device-roles/${deviceId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        loadUserDeviceRoles(userId);
      }
    } catch (error) {
      console.error('Failed to remove device role:', error);
    }
  };

  const createOrganization = async () => {
    if (!newOrgName.trim()) return;
    try {
      const response = await secureFetch('/admin/create-organization', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newOrgName })
      });
      if (response.ok) {
        setNewOrgName('');
        setCreateOrgDialog(false);
        loadOrganizations();
      }
    } catch (error) {
      console.error('Failed to create organization:', error);
    }
  };

  const assignUserToOrganization = async (userId, orgId) => {
    try {
      const response = await secureFetch('/admin/assign-user-organization', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, organizationId: orgId })
      });
      if (response.ok) onRefresh();
    } catch (error) {
      console.error('Failed to assign user to organization:', error);
    }
  };

  const assignDeviceToOrganization = async (deviceId, orgId) => {
    try {
      const response = await secureFetch('/admin/assign-device-organization', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId, organizationId: orgId })
      });
      if (response.ok) loadAllDevices();
    } catch (error) {
      console.error('Failed to assign device to organization:', error);
    }
  };

  const assignDevice = async (userId, deviceId) => {
    try {
      const response = await secureFetch('/admin/assign-device', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, deviceId })
      });
      if (response.ok) {
        onRefresh();
        loadAllDevices();
        setEditUserDialog(null);
      }
    } catch (error) {
      console.error('Failed to assign device:', error);
    }
  };

  const unassignDevice = async (userId, deviceId) => {
    try {
      const response = await secureFetch('/admin/unassign-device', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, deviceId })
      });
      if (response.ok) {
        onRefresh();
        loadAllDevices();
      }
    } catch (error) {
      console.error('Failed to unassign device:', error);
    }
  };

  const toggleUserAdmin = async (userId, isAdmin) => {
    try {
      const response = await secureFetch('/admin/toggle-admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, isAdmin: !isAdmin })
      });
      if (response.ok) onRefresh();
    } catch (error) {
      console.error('Failed to toggle admin status:', error);
    }
  };

  const createUser = async () => {
    if (!newUserEmail.trim() || !newUserPassword.trim()) return;
    try {
      const response = await secureFetch('/admin/create-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: newUserEmail, 
          password: newUserPassword,
          organizationId: newUserOrg ? parseInt(newUserOrg) : null
        })
      });
      if (response.ok) {
        setNewUserEmail('');
        setNewUserPassword('');
        setNewUserOrg('');
        setCreateUserDialog(false);
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to create user:', error);
    }
  };

  const resetUserPassword = async () => {
    if (!resetPassword.trim() || !resetPasswordDialog) return;
    try {
      const response = await secureFetch('/admin/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          userId: resetPasswordDialog.Id, 
          newPassword: resetPassword 
        })
      });
      if (response.ok) {
        setResetPassword('');
        setResetPasswordDialog(null);
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
    }
  };

  const deleteUser = async () => {
    if (!deleteUserDialog) return;
    try {
      const response = await secureFetch('/admin/delete-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: deleteUserDialog.Id })
      });
      if (response.ok) {
        setDeleteUserDialog(null);
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const openDevice = (deviceId) => {
    window.open(ensureHttps(`/proxy/${deviceId}/index.html`), '_blank');
  };

  const filteredUsers = users.filter(u => 
    u.Email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDevices = allDevices.filter(d => 
    d.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.DeviceId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOrgs = organizations.filter(o => 
    o.Name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const UsersTab = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setCreateUserDialog(true)}
            variant="contained"
          >
            Create User
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            variant="outlined"
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              {currentUser?.IsSuperAdmin && <TableCell>Organization</TableCell>}
              <TableCell>Devices</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.Id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {user.Email[0].toUpperCase()}
                    </Avatar>
                    <Typography>{user.Email}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {user.IsSuperAdmin ? (
                    <Chip icon={<SuperAdminIcon />} label="Super Admin" color="secondary" size="small" />
                  ) : user.IsAdmin ? (
                    <Chip icon={<AdminIcon />} label="Admin" color="primary" size="small" />
                  ) : (
                    <Chip label="User" size="small" />
                  )}
                </TableCell>
                {currentUser?.IsSuperAdmin && (
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={user.OrganizationId || ''}
                        onChange={(e) => assignUserToOrganization(user.Id, parseInt(e.target.value))}
                      >
                        <MenuItem value="">Unassigned</MenuItem>
                        {organizations.map(org => (
                          <MenuItem key={org.Id} value={org.Id}>{org.Name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                )}
                <TableCell>
                  <Chip 
                    label={user.Devices?.length || 0} 
                    size="small" 
                    onClick={() => setEditUserDialog(user)}
                    clickable
                  />
                </TableCell>
                <TableCell>{new Date(user.Created).toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditUserDialog(user)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => setResetPasswordDialog(user)} color="warning">
                    <EditIcon />
                  </IconButton>
                  {currentUser?.IsSuperAdmin && (
                    <>
                      <IconButton
                        size="small"
                        color={user.IsAdmin ? 'error' : 'primary'}
                        onClick={() => toggleUserAdmin(user.Id, user.IsAdmin)}
                      >
                        {user.IsAdmin ? <PersonAddIcon /> : <AdminIcon />}
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteUserDialog(user)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const DevicesTab = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search devices..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300 }}
        />
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadAllDevices}
          variant="outlined"
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : 'Refresh'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {filteredDevices.map((device) => (
          <Grid item xs={12} sm={6} md={4} key={device.Id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CircleIcon 
                    sx={{ 
                      color: device.IsOnline ? 'success.main' : 'error.main',
                      fontSize: 12,
                      mr: 1
                    }} 
                  />
                  <Typography variant="h6" sx={{ flex: 1 }}>
                    {device.Name}
                  </Typography>
                  {device.IsOnline && (
                    <Chip label="Online" color="success" size="small" />
                  )}
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Device ID: {device.DeviceId}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Last Seen: {new Date(device.LastSeen).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Owner: {device.OwnerEmail || 'Unassigned'}
                </Typography>
                
                {currentUser?.IsSuperAdmin && (
                  <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                    <InputLabel>Organization</InputLabel>
                    <Select
                      value={device.OrganizationId || ''}
                      label="Organization"
                      onChange={(e) => assignDeviceToOrganization(device.Id, parseInt(e.target.value))}
                    >
                      {organizations.map(org => (
                        <MenuItem key={org.Id} value={org.Id}>{org.Name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </CardContent>
              {device.IsOnline && (
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<ComputerIcon />}
                    onClick={() => openDevice(device.DeviceId)}
                  >
                    Open Console
                  </Button>
                </CardActions>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const OrganizationsTab = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search organizations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300 }}
        />
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setCreateOrgDialog(true)}
            variant="contained"
          >
            New Organization
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadOrganizations}
            variant="outlined"
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {filteredOrgs.map((org) => (
          <Grid item xs={12} sm={6} md={4} key={org.Id}>
            <Card sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {org.Name}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3">{org.UserCount}</Typography>
                    <Typography variant="body2">Users</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3">{org.DeviceCount}</Typography>
                    <Typography variant="body2">Devices</Typography>
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ display: 'block', mt: 2, opacity: 0.9 }}>
                  Created {new Date(org.Created).toLocaleDateString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const filteredRoles = roles.filter(r =>
    r.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.Description && r.Description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Common permission paths that can be assigned to roles
  const availablePermissions = [
    { path: 'projector', label: 'Projector Settings (All)', description: 'Full projector control' },
    { path: 'projector.settings', label: 'General Settings' },
    { path: 'projector.lens', label: 'Lens Control' },
    { path: 'projector.image', label: 'Image Settings' },
    { path: 'projector.power', label: 'Power Control' },
    { path: 'projector.source', label: 'Source Selection' },
    { path: 'network', label: 'Network Settings (All)', description: 'Full network control' },
    { path: 'network.hotspot', label: 'Hotspot Settings' },
    { path: 'network.internet', label: 'Internet Settings' },
    { path: 'apps', label: 'Apps (All)', description: 'Full app control' },
    { path: 'apps.logging', label: 'View Logs' },
    { path: 'apps.favorites', label: 'Manage Favorites' },
    { path: 'apps.recovery', label: 'Recovery Mode' },
    { path: 'schedule', label: 'Scheduling' },
    { path: 'system', label: 'System (All)', description: 'Full system control' },
    { path: 'system.display', label: 'Display Settings' },
    { path: 'system.password', label: 'Password Management' },
    { path: 'system.time', label: 'Time Settings' },
    { path: 'cloud', label: 'Cloud Features (All)', description: 'Full cloud control' },
    { path: 'cloud.liveView', label: 'Live View' },
    { path: 'cloud.support', label: 'Support Access' },
    { path: 'status', label: 'View Status' },
    { path: 'status.login', label: 'Login Status' },
    { path: 'admin.system', label: 'Admin System Controls' },
    { path: 'admin.roles', label: 'Role Management' },
  ];

  const RolesTab = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search roles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300 }}
        />
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setCreateRoleDialog(true)}
            variant="contained"
          >
            Create Role
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadRoles}
            variant="outlined"
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {filteredRoles.map((role) => (
          <Grid item xs={12} sm={6} md={4} key={role.Id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ flex: 1 }}>
                    {role.Name}
                  </Typography>
                  {role.IsSystemRole && (
                    <Chip label="System" size="small" color="secondary" />
                  )}
                </Box>

                {role.Description && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {role.Description}
                  </Typography>
                )}

                <Divider sx={{ my: 1 }} />

                <Typography variant="body2" color="text.secondary">
                  Permissions: {role.Permissions?.length || 0}
                </Typography>

                {role.Permissions?.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {role.Permissions.slice(0, 3).map((perm, idx) => (
                      <Chip key={idx} label={perm} size="small" variant="outlined" />
                    ))}
                    {role.Permissions.length > 3 && (
                      <Chip label={`+${role.Permissions.length - 3} more`} size="small" />
                    )}
                  </Box>
                )}
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<SecurityIcon />}
                  onClick={() => {
                    setEditPermissionsDialog(role);
                    setSelectedPermissions(role.Permissions || []);
                  }}
                  disabled={role.Name === 'admin'}
                >
                  Permissions
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setEditRoleDialog(role);
                    setNewRoleName(role.Name);
                    setNewRoleDescription(role.Description || '');
                  }}
                  disabled={role.IsSystemRole && !currentUser?.IsSuperAdmin}
                >
                  Edit
                </Button>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteRoleDialog(role)}
                  disabled={role.IsSystemRole}
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          User Role Assignments
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Default Role</TableCell>
                <TableCell>Device Overrides</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.slice(0, 10).map((user) => (
                <TableRow key={user.Id} hover>
                  <TableCell>{user.Email}</TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={user.RoleId || ''}
                        onChange={(e) => e.target.value ? assignUserRole(user.Id, parseInt(e.target.value)) : removeUserRole(user.Id)}
                        displayEmpty
                      >
                        <MenuItem value="">No Role</MenuItem>
                        {roles.map(role => (
                          <MenuItem key={role.Id} value={role.Id}>{role.Name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => {
                        setUserDeviceRolesDialog(user);
                        loadUserDeviceRoles(user.Id);
                      }}
                    >
                      Manage
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setAssignRoleDialog(user);
                        setSelectedRoleId(user.RoleId?.toString() || '');
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        p: 3,
        borderRadius: 2
      }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AdminIcon fontSize="large" />
          Admin Panel
          {currentUser?.IsSuperAdmin && (
            <Chip label="Super Admin" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
          )}
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => { setActiveTab(v); setSearchTerm(''); }}>
          <Tab icon={<PersonAddIcon />} label="Users" />
          <Tab icon={<ComputerIcon />} label="Devices" />
          {currentUser?.IsSuperAdmin && <Tab icon={<BusinessIcon />} label="Organizations" />}
          <Tab icon={<SecurityIcon />} label="Roles" />
          {currentUser?.IsSuperAdmin && <Tab icon={<DeploymentIcon />} label="Deployment" />}
        </Tabs>
      </Paper>

      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && <UsersTab />}
        {activeTab === 1 && <DevicesTab />}
        {activeTab === 2 && currentUser?.IsSuperAdmin && <OrganizationsTab />}
        {((currentUser?.IsSuperAdmin && activeTab === 3) || (!currentUser?.IsSuperAdmin && activeTab === 2)) && <RolesTab />}
        {currentUser?.IsSuperAdmin && activeTab === 4 && <DeploymentTab />}
      </Box>

      {/* Edit User Dialog */}
      <Dialog 
        open={!!editUserDialog} 
        onClose={() => setEditUserDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Manage Devices for {editUserDialog?.Email}
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Assigned Devices
          </Typography>
          <List>
            {editUserDialog?.Devices?.map(device => (
              <ListItem key={device.Id}>
                <ListItemText
                  primary={device.Name}
                  secondary={device.IsOnline ? 'Online' : 'Offline'}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => unassignDevice(editUserDialog.Id, device.Id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            )) || <Typography color="text.secondary">No devices assigned</Typography>}
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Available Devices
          </Typography>
          <List>
            {allDevices
              .filter(device => 
                !editUserDialog?.Devices?.some(d => d.Id === device.Id) &&
                (currentUser?.IsSuperAdmin || device.OrganizationId === editUserDialog?.OrganizationId)
              )
              .map(device => (
                <ListItem key={device.Id}>
                  <ListItemText
                    primary={device.Name}
                    secondary={device.IsOnline ? 'Online' : 'Offline'}
                  />
                  <ListItemSecondaryAction>
                    <Button
                      size="small"
                      disabled={!device.IsOnline}
                      onClick={() => assignDevice(editUserDialog.Id, device.Id)}
                    >
                      Assign
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Organization Dialog */}
      <Dialog open={createOrgDialog} onClose={() => setCreateOrgDialog(false)}>
        <DialogTitle>Create Organization</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Organization Name"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOrgDialog(false)}>Cancel</Button>
          <Button onClick={createOrganization} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialog} onClose={() => setCreateUserDialog(false)}>
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Email"
            type="email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={newUserPassword}
            onChange={(e) => setNewUserPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          {currentUser?.IsSuperAdmin && (
            <FormControl fullWidth>
              <InputLabel>Organization</InputLabel>
              <Select
                value={newUserOrg}
                label="Organization"
                onChange={(e) => setNewUserOrg(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {organizations.map(org => (
                  <MenuItem key={org.Id} value={org.Id}>{org.Name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUserDialog(false)}>Cancel</Button>
          <Button onClick={createUser} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordDialog} onClose={() => setResetPasswordDialog(null)}>
        <DialogTitle>Reset Password for {resetPasswordDialog?.Email}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="New Password"
            type="password"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialog(null)}>Cancel</Button>
          <Button onClick={resetUserPassword} variant="contained">Reset</Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={!!deleteUserDialog} onClose={() => setDeleteUserDialog(null)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteUserDialog?.Email}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserDialog(null)}>Cancel</Button>
          <Button onClick={deleteUser} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Create Role Dialog */}
      <Dialog open={createRoleDialog} onClose={() => setCreateRoleDialog(false)}>
        <DialogTitle>Create Role</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Role Name"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={newRoleDescription}
            onChange={(e) => setNewRoleDescription(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateRoleDialog(false); setNewRoleName(''); setNewRoleDescription(''); }}>Cancel</Button>
          <Button onClick={createRole} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editRoleDialog} onClose={() => setEditRoleDialog(null)}>
        <DialogTitle>Edit Role: {editRoleDialog?.Name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Role Name"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={newRoleDescription}
            onChange={(e) => setNewRoleDescription(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditRoleDialog(null); setNewRoleName(''); setNewRoleDescription(''); }}>Cancel</Button>
          <Button onClick={updateRole} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Role Dialog */}
      <Dialog open={!!deleteRoleDialog} onClose={() => setDeleteRoleDialog(null)}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role "{deleteRoleDialog?.Name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Users with this role will lose access to devices until a new role is assigned.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRoleDialog(null)}>Cancel</Button>
          <Button onClick={deleteRole} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog
        open={!!editPermissionsDialog}
        onClose={() => setEditPermissionsDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Permissions for: {editPermissionsDialog?.Name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
            Select which features this role can access. Parent permissions grant access to all child features.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={1}>
            {availablePermissions.map((perm) => (
              <Grid item xs={12} sm={6} key={perm.path}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    border: '1px solid',
                    borderColor: selectedPermissions.includes(perm.path) ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: selectedPermissions.includes(perm.path) ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => {
                    if (selectedPermissions.includes(perm.path)) {
                      setSelectedPermissions(selectedPermissions.filter(p => p !== perm.path));
                    } else {
                      setSelectedPermissions([...selectedPermissions, perm.path]);
                    }
                  }}
                >
                  {selectedPermissions.includes(perm.path) ? (
                    <CheckBoxIcon color="primary" sx={{ mr: 1 }} />
                  ) : (
                    <CheckBoxOutlineBlankIcon sx={{ mr: 1 }} />
                  )}
                  <Box>
                    <Typography variant="body2">{perm.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {perm.path}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditPermissionsDialog(null); setSelectedPermissions([]); }}>Cancel</Button>
          <Button onClick={updateRolePermissions} variant="contained">Save Permissions</Button>
        </DialogActions>
      </Dialog>

      {/* User Device Roles Dialog */}
      <Dialog
        open={!!userDeviceRolesDialog}
        onClose={() => setUserDeviceRolesDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Device Role Overrides for: {userDeviceRolesDialog?.Email}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
            Set different roles for specific devices. If no override is set, the user's default role is used.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <List>
            {allDevices
              .filter(d => currentUser?.IsSuperAdmin || d.OrganizationId === userDeviceRolesDialog?.OrganizationId)
              .map((device) => {
                const deviceRole = userDeviceRoles.find(dr => dr.DeviceId === device.DeviceId);
                return (
                  <ListItem key={device.Id}>
                    <ListItemText
                      primary={device.Name}
                      secondary={device.DeviceId}
                    />
                    <FormControl size="small" sx={{ minWidth: 150, mr: 1 }}>
                      <Select
                        value={deviceRole?.RoleId || ''}
                        onChange={(e) => {
                          if (e.target.value) {
                            setUserDeviceRole(userDeviceRolesDialog.Id, device.DeviceId, parseInt(e.target.value));
                          } else {
                            removeUserDeviceRole(userDeviceRolesDialog.Id, device.DeviceId);
                          }
                        }}
                        displayEmpty
                      >
                        <MenuItem value="">Use Default Role</MenuItem>
                        {roles.map(role => (
                          <MenuItem key={role.Id} value={role.Id}>{role.Name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {deviceRole && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeUserDeviceRole(userDeviceRolesDialog.Id, device.DeviceId)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </ListItem>
                );
              })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDeviceRolesDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}